/**
 * saas-telemetry.ts — Errors / RUM / Cron-heartbeat surfaces of the SaaS API
 *
 * Purpose:    Typed client + wire mapping for the telemetry panels the spec
 *             documents on the gateway: GET /v1/errors (+resolve), GET /v1/rum,
 *             GET/POST /v1/cron. These routes are being added in parallel
 *             (G-GATEWAY) — every call passes 404/405/501 through so pages
 *             render a "coming online" panel via isComingOnline, never a crash.
 * Inputs:     request/withMockFallback/mapResult from ./saas-client
 * Outputs:    errorsApi / rumApi / cronApi returning Result<T, SentryError>
 * Constraints: Wire shapes are enveloped snake_case (gateway contract);
 *              mappers tolerate absent fields. Mock data is static demo state
 *              (module-level, mutation-capable) for VITE_API_MOCK/offline.
 * SOT:        nsentry SaaS completion spec § G-WEB / § G-GATEWAY endpoints
 */
import { ok, err, type Result } from './result'
import type { SentryError } from './sentry-error'
import { request, withMockFallback, mapResult } from './saas-client'

// ─── View models ─────────────────────────────────────────────────────────────

export interface ErrorGroup {
  id: string
  message: string
  culprit: string | null
  count: number
  firstSeen: string | null
  lastSeen: string | null
  status: 'open' | 'resolved'
}

export interface RumSummary {
  pageviewsThisMonth: number
  p75LcpMs: number | null
  p75InpMs: number | null
  errorRatePct: number | null
  topPages: Array<{ path: string; views: number }>
}

export interface Heartbeat {
  id: string
  name: string
  intervalSeconds: number
  status: 'healthy' | 'late' | 'missing' | 'pending'
  lastPingAt: string | null
}

// ─── Wire shapes (enveloped snake_case) ──────────────────────────────────────

export interface WireErrorGroup {
  id: string
  message: string
  culprit?: string | null
  count?: number
  first_seen?: string | null
  last_seen?: string | null
  status?: string
}

export interface WireRum {
  pageviews_this_month?: number
  p75_lcp_ms?: number | null
  p75_inp_ms?: number | null
  error_rate_pct?: number | null
  top_pages?: Array<{ path: string; views: number }>
}

export interface WireHeartbeat {
  id: string
  name: string
  interval_seconds?: number
  status?: string
  last_ping_at?: string | null
}

// ─── Mappers (pure, fixture-testable) ────────────────────────────────────────

export function mapErrorGroup(w: WireErrorGroup): ErrorGroup {
  return {
    id: w.id,
    message: w.message,
    culprit: w.culprit ?? null,
    count: w.count ?? 0,
    firstSeen: w.first_seen ?? null,
    lastSeen: w.last_seen ?? null,
    status: w.status === 'resolved' ? 'resolved' : 'open',
  }
}

export function mapErrorGroups(body: { errors?: WireErrorGroup[] }): ErrorGroup[] {
  return (body.errors ?? []).map(mapErrorGroup)
}

export function mapRum(body: { rum?: WireRum }): RumSummary {
  const r = body.rum
  return {
    pageviewsThisMonth: r?.pageviews_this_month ?? 0,
    p75LcpMs: r?.p75_lcp_ms ?? null,
    p75InpMs: r?.p75_inp_ms ?? null,
    errorRatePct: r?.error_rate_pct ?? null,
    topPages: r?.top_pages ?? [],
  }
}

const HEARTBEAT_STATUSES = new Set(['healthy', 'late', 'missing', 'pending'])

export function mapHeartbeat(w: WireHeartbeat): Heartbeat {
  return {
    id: w.id,
    name: w.name,
    intervalSeconds: w.interval_seconds ?? 0,
    status: (HEARTBEAT_STATUSES.has(w.status ?? '') ? w.status : 'pending') as Heartbeat['status'],
    lastPingAt: w.last_ping_at ?? null,
  }
}

export function mapHeartbeats(body: { heartbeats?: WireHeartbeat[] }): Heartbeat[] {
  return (body.heartbeats ?? []).map(mapHeartbeat)
}

/** Public ping URL a job curls to report a heartbeat (documented POST route). */
export function heartbeatPingUrl(id: string): string {
  return `https://api.sentry.nself.org/v1/cron/${id}/ping`
}

// ─── Mock state (demo/offline only) ──────────────────────────────────────────

const now = () => new Date().toISOString()

const mockErrors: ErrorGroup[] = [
  { id: 'err-1', message: "TypeError: Cannot read properties of undefined (reading 'map')", culprit: 'DashboardPage.tsx', count: 412, firstSeen: now(), lastSeen: now(), status: 'open' },
  { id: 'err-2', message: 'NetworkError: fetch failed', culprit: 'saas-client.ts', count: 201, firstSeen: now(), lastSeen: now(), status: 'open' },
]

const mockHeartbeats: Heartbeat[] = [
  { id: 'hb-1', name: 'nightly-backup', intervalSeconds: 86_400, status: 'healthy', lastPingAt: now() },
  { id: 'hb-2', name: 'usage-rollup', intervalSeconds: 3_600, status: 'late', lastPingAt: now() },
]

function mockNotFound(what: string): Result<never, SentryError> {
  return err({ type: 'config_error', message: `${what} not found` })
}

// ─── APIs ────────────────────────────────────────────────────────────────────

export const errorsApi = {
  list: () =>
    withMockFallback(
      async () => mapResult(await request<{ errors?: WireErrorGroup[] }>('/v1/errors'), mapErrorGroups),
      () => ok([...mockErrors]),
    ),
  resolve: (id: string) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ error?: WireErrorGroup }>(`/v1/errors/${id}/resolve`, { method: 'POST' }),
          (body) => (body.error ? mapErrorGroup(body.error) : ({ id, status: 'resolved' } as Partial<ErrorGroup> as ErrorGroup)),
        ),
      () => {
        const found = mockErrors.find((e) => e.id === id)
        if (!found) return mockNotFound('Error group')
        found.status = 'resolved'
        return ok({ ...found })
      },
    ),
}

export const rumApi = {
  get: () =>
    withMockFallback(
      async () => mapResult(await request<{ rum?: WireRum }>('/v1/rum'), mapRum),
      () =>
        ok({
          pageviewsThisMonth: 3_920,
          p75LcpMs: 1840,
          p75InpMs: 140,
          errorRatePct: 0.6,
          topPages: [
            { path: '/', views: 2_115 },
            { path: '/pricing', views: 830 },
            { path: '/docs', views: 405 },
          ],
        }),
    ),
}

export const cronApi = {
  list: () =>
    withMockFallback(
      async () => mapResult(await request<{ heartbeats?: WireHeartbeat[] }>('/v1/cron'), mapHeartbeats),
      () => ok([...mockHeartbeats]),
    ),
  create: (input: { name: string; intervalSeconds: number }) =>
    withMockFallback(
      async () =>
        mapResult(
          await request<{ heartbeat: WireHeartbeat }>('/v1/cron', {
            method: 'POST',
            body: JSON.stringify({ name: input.name, interval_seconds: input.intervalSeconds }),
          }),
          (body) => mapHeartbeat(body.heartbeat),
        ),
      () => {
        const hb: Heartbeat = {
          id: `hb-${mockHeartbeats.length + 1}`,
          name: input.name,
          intervalSeconds: input.intervalSeconds,
          status: 'pending',
          lastPingAt: null,
        }
        mockHeartbeats.push(hb)
        return ok({ ...hb })
      },
    ),
  remove: (id: string) =>
    withMockFallback(
      () => request<void>(`/v1/cron/${id}`, { method: 'DELETE' }),
      () => {
        const idx = mockHeartbeats.findIndex((h) => h.id === id)
        if (idx === -1) return mockNotFound('Heartbeat')
        mockHeartbeats.splice(idx, 1)
        return ok(undefined)
      },
    ),
}
